import { useEffect, useState } from "react";
import { ScreenLoading, Warn } from "../components";
import { api, evidenceUrl, type DocumentCategoryGroup } from "../lib/api";
import { addressTitle, fmtBytes, fmtDateTime, placeLine } from "../lib/status";

/**
 * The Database: every uploaded legal PDF under its fixed category.
 *
 * Two categories, no more: Telkomsel areas must produce the PKS DEVELOPER
 * document, TIF areas the SITAC ODC document (ADR 0008). Each file links
 * to its area(s); an area with no landed file is listed as missing its
 * document, visibly -- never silently complete.
 *
 * Read-only by capability like the dashboard: anyone holding area.read
 * lists the metadata, but the download link renders only where
 * evidence.open allows opening the bytes.
 */
export function Database({ onOpen, canOpen }: { onOpen: (areaId: number) => void; canOpen: boolean }) {
  const [groups, setGroups] = useState<DocumentCategoryGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void api
      .documents()
      .then((r) => live && setGroups(r))
      .catch((e) => live && setError(e instanceof Error ? e.message : "Gagal memuat database."));
    return () => {
      live = false;
    };
  }, []);

  if (error)
    return (
      <div className="page">
        <Warn tone="red">{error}</Warn>
      </div>
    );
  if (!groups)
    return (
      <div className="page">
        <ScreenLoading label="Memuat database…" />
      </div>
    );

  return (
    <div className="page">
      <h1 className="page-title">Database</h1>
      <p className="page-sub">Dokumen legal yang sudah diunggah, per kategori dokumen.</p>

      {groups.map((g) => (
        <section key={g.category} aria-label={`Kategori ${g.category}`}>
          <h2 className="page-sub" style={{ fontSize: 16, margin: "20px 0 8px", lineHeight: 1.35 }}>
            {g.category}
            <br />
            <span className="small mute">{g.document_label}</span>
          </h2>

          {g.files.length === 0 && g.missing.length === 0 ? (
            <div className="panel">
              <p className="mute small" style={{ margin: 0 }}>
                Belum ada area {g.category}.
              </p>
            </div>
          ) : (
            // Both presentations render; the breakpoint picks, per the User
            // page precedent: a table cannot fit a phone, cards waste a
            // desktop, and the file count is small so the duplicated DOM
            // costs nothing.
            <>
              <div className="panel only-desktop">
                <table>
                  <thead>
                    <tr>
                      <th>Dokumen</th>
                      <th>Area</th>
                      <th>Diunggah</th>
                      <th style={{ textAlign: "right" }}>Berkas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.files.map((f) => (
                      <tr key={f.evidence_id}>
                        <td>
                          <span className="chip chip-granted">{g.document_label}</span>{" "}
                          <span className="small mute mono">{fmtBytes(f.byte_size)}</span>
                        </td>
                        <td>
                          {f.areas.map((a) => (
                            <div key={a.area_id}>
                              <button className="btn btn-sm btn-ghost" onClick={() => onOpen(a.area_id)}>
                                {addressTitle(a)}
                              </button>{" "}
                              <span className="small mute">{placeLine(a)}</span>
                            </div>
                          ))}
                        </td>
                        <td className="small mute" title={fmtDateTime(f.created_at)}>
                          {f.author ?? "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {canOpen ? (
                            <a
                              className="btn btn-sm"
                              href={evidenceUrl(f.evidence_id)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Unduh PDF
                            </a>
                          ) : (
                            <span className="small mute" title="Akses bukti tidak tersedia">
                              🔒 terkunci
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {g.missing.map((m) => (
                      <tr key={`missing-${m.area_id}`}>
                        <td>
                          <span className="chip chip-pending">Belum ada dokumen</span>{" "}
                          <span className="small mute">{m.document_label} belum diunggah</span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-ghost" onClick={() => onOpen(m.area_id)}>
                            {addressTitle(m)}
                          </button>{" "}
                          <span className="small mute">{placeLine(m)}</span>
                        </td>
                        <td className="small mute">—</td>
                        <td style={{ textAlign: "right" }} className="small mute">
                          —
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="stack only-phone">
                {g.files.map((f) => (
                  <div className="panel" key={f.evidence_id}>
                    <div className="rowflex wrap">
                      <span className="chip chip-granted">{g.document_label}</span>
                      <span className="spacer" />
                      <span className="small mute mono">{fmtBytes(f.byte_size)}</span>
                    </div>
                    {f.areas.map((a) => (
                      <div key={a.area_id} style={{ margin: "8px 0" }}>
                        <button className="btn btn-sm btn-ghost" onClick={() => onOpen(a.area_id)}>
                          {addressTitle(a)}
                        </button>
                        <div className="small mute">{placeLine(a)}</div>
                      </div>
                    ))}
                    <div className="rowflex wrap">
                      <span className="small mute">oleh {f.author ?? "—"}</span>
                      <span className="spacer" />
                      {canOpen ? (
                        <a className="btn btn-sm" href={evidenceUrl(f.evidence_id)} target="_blank" rel="noreferrer">
                          Unduh PDF
                        </a>
                      ) : (
                        <span className="small mute">🔒 terkunci</span>
                      )}
                    </div>
                  </div>
                ))}
                {g.missing.map((m) => (
                  <div className="panel" key={`missing-${m.area_id}`}>
                    <div className="rowflex wrap">
                      <span className="chip chip-pending">Belum ada dokumen</span>
                    </div>
                    <div style={{ margin: "8px 0" }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => onOpen(m.area_id)}>
                        {addressTitle(m)}
                      </button>
                      <div className="small mute">{placeLine(m)}</div>
                    </div>
                    <div className="small mute">{m.document_label} belum diunggah untuk area ini.</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      ))}
    </div>
  );
}
